// Dashboard.js — CSS Module pattern (identique à Conversation.js)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConversations } from '../context/ConversationContext';
import { 
  Plus, MessageCircle, Trash2, MoreVertical, CreditCard,
  Zap, Brain, Wand2, Calendar, Search, AlertCircle
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import SubscriptionManager from '../components/SubscriptionManager';
import styles from './Dashboard.module.css';

const Dashboard = () => {
  const { t, i18n } = useTranslation(['dashboard', 'common', 'errors']);
  const navigate = useNavigate();

  // Logs de débogage au montage
  useEffect(() => {
    console.log('📊 === DASHBOARD - COMPONENT MOUNT ===');
    console.log('📍 URL:', window.location.href);
    console.log('🌍 Language:', i18n.language);
    console.log('🔑 Token present?', !!localStorage.getItem('token'));
    console.log('👤 User present?', !!localStorage.getItem('user'));

    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token) {
      console.log('✅ Token:', token.substring(0, 30) + '...');
    } else {
      console.log('❌ NO TOKEN FOUND');
    }

    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        console.log('✅ User:', user);
      } catch (e) {
        console.error('❌ Error parsing user:', e);
      }
    } else {
      console.log('❌ NO USER FOUND');
    }
  }, [i18n.language]);

  const { 
    getAllConversations, 
    deleteConversation, 
    updateConversationStatus, 
    user, 
    loading,
    refreshData 
  } = useConversations();

  useEffect(() => {
    console.log('📊 useConversations - State:', { user, loading, hasUser: !!user });
    if (!user && !loading) {
      console.log('⚠️ useConversations did not return user');
    }
  }, [user, loading]);

  const conversations = getAllConversations();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('conversations');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');

  // Fermer sidebar au changement d'onglet
  useEffect(() => { setSidebarOpen(false); }, [activeTab]);

  // Bloquer scroll body quand sidebar ouverte (mobile)
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  // Filtrer les conversations
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch =
      conv.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getConversationPreview(conv).toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || conv.status === selectedStatus;
    return matchesSearch && matchesStatus;
  });

  // ── Handlers ──
  const handleNewConversation = () => {
    if (user?.credits <= 0) {
      alert(t('dashboard:alerts.no_credits'));
      setActiveTab('subscription');
      return;
    }
    navigate('/conversation/new');
  };

  const handleOpenConversation = (conversationId) => {
    navigate(`/conversation/${conversationId}`);
  };

  const handleDeleteConversation = async (conversationId) => {
    try {
      const success = await deleteConversation(conversationId);
      if (success) {
        setShowDeleteConfirm(null);
        await refreshData();
      } else {
        alert(t('errors:conversation.delete_failed'));
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert(t('errors:conversation.delete_failed'));
    }
  };

  const handleStatusChange = async (conversationId, newStatus) => {
    try {
      const success = await updateConversationStatus(conversationId, newStatus);
      if (success) await refreshData();
    } catch (error) {
      console.error('Status update error:', error);
    }
  };

  // ── Utility functions ──
  const getStatusIcon = (status) => {
    const icons = { success: '✅', pending: '⏳', active: '💬', closed: '❌', default: '🆕' };
    return icons[status] || icons.default;
  };

  const getStatusColor = (status) => {
    const colors = {
      success: '#10b981', pending: '#f59e0b',
      active: '#3b82f6', closed: '#ef4444', default: '#6b7280'
    };
    return colors[status] || colors.default;
  };

  const getConversationPreview = (conversation) => {
    if (conversation.girl_info?.nom) {
      return conversation.girl_info.nom + (conversation.girl_info?.age
        ? `, ${conversation.girl_info.age} ${t('dashboard:years_old')}` : '');
    }
    return conversation.girl_info?.bio?.substring(0, 80) + '...' || t('dashboard:no_message');
  };

  const getPremiumStats = () => {
    const stats = { sentimentAnalysisUsed: 0, reformulationUsed: 0, totalPremiumCredits: 0 };
    if (user?.stats?.monthly_usage) {
      user.stats.monthly_usage.forEach(usage => {
        if (usage.feature_type === 'sentiment_analysis') {
          stats.sentimentAnalysisUsed = usage.usage_count;
          stats.totalPremiumCredits += usage.credits_spent;
        }
        if (usage.feature_type === 'message_reformulate') {
          stats.reformulationUsed = usage.usage_count;
          stats.totalPremiumCredits += usage.credits_spent;
        }
      });
    }
    return stats;
  };

  const premiumStats = getPremiumStats();

  // ── Loading state ──
  if (loading) {
    return (
      <div className={styles["loading-container"]}>
        <div className={styles["loading-spinner"]}></div>
        <p className={styles["loading-text"]}>{t('dashboard:loading')}</p>
      </div>
    );
  }

  // ── Render ──
  return (
    <div className={`${styles["dashboard-layout"]} ${styles[`dashboard-${activeTab}`] || ''}`}>

      {/* Mobile hamburger */}
      {activeTab !== 'subscription' && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className={styles["mobile-menu-toggle"]}
          aria-label={t('dashboard:menu.open')}
        >
          <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Sidebar */}
      <Sidebar
        user={user}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      {/* Main content */}
      <div className={`${styles["main-content-area"]} ${styles[`content-${activeTab}`] || ''}`}>

        {/* ── CONVERSATIONS TAB ── */}
        {activeTab === 'conversations' ? (
          <div className={styles["conversations-content"]}>

            {/* Header */}
            <div className={styles["content-header"]}>
              <div className={styles["header-info"]}>
                <h1 className={styles["page-title"]}>{t('dashboard:header.title')}</h1>
                <div className={styles["stats-overview"]}>
                  <div className={styles["stat-item"]}>
                    <MessageCircle size={16} />
                    <span>
                      {conversations.length} {conversations.length > 1
                        ? t('dashboard:stats.conversations')
                        : t('dashboard:stats.conversation')}
                    </span>
                  </div>
                  <div className={styles["stat-item"]}>
                    <Zap size={16} />
                    <span>{user?.used_credits || 0} {t('dashboard:stats.credits_used')}</span>
                  </div>
                  {user?.subscription?.features?.includes('sentiment_analysis') && (
                    <div className={`${styles["stat-item"]} ${styles["stat-premium"]}`}>
                      <Brain size={16} />
                      <span>{premiumStats.sentimentAnalysisUsed} {t('dashboard:stats.analyses')}</span>
                    </div>
                  )}
                  {user?.subscription?.features?.includes('message_reformulate') && (
                    <div className={`${styles["stat-item"]} ${styles["stat-premium"]}`}>
                      <Wand2 size={16} />
                      <span>{premiumStats.reformulationUsed} {t('dashboard:stats.reformulations')}</span>
                    </div>
                  )}
                </div>
              </div>

              <button onClick={handleNewConversation} className={styles["new-conversation-button"]}>
                <Plus size={20} />
                <span>{t('dashboard:header.new_conversation')}</span>
              </button>
            </div>

            {/* Filters */}
            <div className={styles["content-filters"]}>
              <div className={styles["search-wrapper"]}>
                <Search className={styles["search-icon"]} />
                <input
                  type="text"
                  placeholder={t('dashboard:search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles["search-input"]}
                />
              </div>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className={styles["status-filter"]}
              >
                <option value="all">{t('dashboard:filters.all')}</option>
                <option value="active">{t('dashboard:filters.active')}</option>
                <option value="pending">{t('dashboard:filters.pending')}</option>
                <option value="success">{t('dashboard:filters.success')}</option>
                <option value="closed">{t('dashboard:filters.closed')}</option>
              </select>
            </div>

            {/* Conversations grid */}
            {filteredConversations.length === 0 ? (
              <div className={styles["empty-state"]}>
                <MessageCircle className={styles["empty-icon"]} />
                <h3 className={styles["empty-title"]}>
                  {searchTerm ? t('dashboard:empty.no_results') : t('dashboard:empty.no_conversations')}
                </h3>
                <p className={styles["empty-description"]}>
                  {searchTerm
                    ? t('dashboard:empty.try_other_keywords')
                    : t('dashboard:empty.create_first')}
                </p>
                {!searchTerm && (
                  <button onClick={handleNewConversation} className={styles["empty-cta-button"]}>
                    <Plus size={20} />
                    {t('dashboard:empty.create_button')}
                  </button>
                )}
              </div>
            ) : (
              <div className={styles["conversations-grid"]}>
                {filteredConversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    onClick={() => handleOpenConversation(conversation.id)}
                    className={styles["conversation-card"]}
                  >
                    {/* Card header */}
                    <div className={styles["card-header"]}>
                      <div
                        className={styles["status-badge"]}
                        style={{
                          backgroundColor: getStatusColor(conversation.status) + '22',
                          color: getStatusColor(conversation.status)
                        }}
                      >
                        <span>{getStatusIcon(conversation.status)}</span>
                        <span className={styles["status-text"]}>
                          {conversation.status
                            ? t(`dashboard:status.${conversation.status}`)
                            : t('dashboard:status.new')}
                        </span>
                      </div>

                      <div className={styles["card-actions"]}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowDeleteConfirm(conversation.id);
                          }}
                          className={styles["actions-button"]}
                          aria-label={t('common:buttons.options')}
                        >
                          <MoreVertical size={16} />
                        </button>
                      </div>
                    </div>

                    {/* Card body */}
                    <h3 className={styles["conversation-title"]}>{conversation.title}</h3>
                    <p className={styles["conversation-preview"]}>{getConversationPreview(conversation)}</p>

                    {/* Card footer */}
                    <div className={styles["card-footer"]}>
                      <div className={styles["conversation-date"]}>
                        <Calendar size={14} />
                        <span>
                          {new Date(conversation.created_at).toLocaleDateString(
                            i18n.language === 'fr' ? 'fr-FR' : 'en-US'
                          )}
                        </span>
                      </div>
                      <div className={styles["conversation-stats"]}>
                        <div className={styles["stat"]}>
                          <MessageCircle size={14} />
                          <span>{conversation.message_count || 0}</span>
                        </div>
                        <div className={styles["stat"]}>
                          <CreditCard size={14} />
                          <span>{conversation.credits_used || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        ) : activeTab === 'subscription' ? (
          <div className={styles["subscription-content"]}>
            <SubscriptionManager user={user} onUpgrade={refreshData} />
          </div>

        ) : activeTab === 'stats' ? (
          <div className={styles["other-content"]}>
            <div className={styles["under-construction"]}>
              <div className={styles["construction-icon"]}>📊</div>
              <h2 className={styles["construction-title"]}>{t('dashboard:construction.stats_title')}</h2>
              <p className={styles["construction-description"]}>{t('dashboard:construction.stats_desc')}</p>
            </div>
          </div>

        ) : activeTab === 'history' ? (
          <div className={styles["other-content"]}>
            <div className={styles["under-construction"]}>
              <div className={styles["construction-icon"]}>📜</div>
              <h2 className={styles["construction-title"]}>{t('dashboard:construction.history_title')}</h2>
              <p className={styles["construction-description"]}>{t('dashboard:construction.history_desc')}</p>
            </div>
          </div>

        ) : (
          <div className={styles["other-content"]}>
            <div className={styles["under-construction"]}>
              <div className={styles["construction-icon"]}>🚧</div>
              <h2 className={styles["construction-title"]}>{t('dashboard:construction.generic_title')}</h2>
              <p className={styles["construction-description"]}>{t('dashboard:construction.generic_desc')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className={styles["modal-overlay"]} onClick={() => setShowDeleteConfirm(null)}>
          <div className={styles["modal-content"]} onClick={(e) => e.stopPropagation()}>
            <div className={styles["modal-header"]}>
              <div className={styles["modal-icon"]}>
                <AlertCircle size={20} />
              </div>
              <h3 className={styles["modal-title"]}>{t('dashboard:modal.delete_title')}</h3>
            </div>
            <div className={styles["modal-body"]}>
              <p>{t('dashboard:modal.delete_message')}</p>
            </div>
            <div className={styles["modal-actions"]}>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className={`${styles["modal-button"]} ${styles["modal-cancel"]}`}
              >
                {t('common:buttons.cancel')}
              </button>
              <button
                onClick={() => handleDeleteConversation(showDeleteConfirm)}
                className={`${styles["modal-button"]} ${styles["modal-confirm"]}`}
              >
                <Trash2 size={16} />
                {t('common:buttons.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;